import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import EmailTemplateEditor from "@/components/staff/EmailTemplateEditor";
import "@/ch.css";

const LANGS = { en: "English", sw: "Kiswahili" };
const fmt = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const StatusChip = ({ l, id }) => {
  const c = l.status === "active" ? (l.source === "builtin" ? "var(--mu)" : "var(--ok, #16a34a)") : "var(--ng)";
  const label = l.status === "active" ? (l.source === "builtin" ? `built-in v${l.version}` : `edited v${l.version}`) : "archived · built-in used";
  return <span className="chip" style={{ "--c": c }} data-testid={id}>{label}{l.drafts ? ` · ${l.drafts} draft` : ""}</span>;
};

function Switch({ d, reload }) {
  const flip = () => {
    const note = window.prompt(d.enabled ? "Reason for turning edited templates off (all emails will use built-in wording):" : "Reason for turning edited templates back on:");
    if (!note || note.trim().length < 3) return;
    api.put("/email-templates/settings", { enabled: !d.enabled, note }).then(() => { toast.success("Saved"); reload(); }).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <section className="box" style={{ padding: "10px 14px", marginBottom: 12 }} data-testid="etpl-switch">
      <div className="r sp" style={{ gap: 8, flexWrap: "wrap" }}>
        <span><b>Edited templates are {d.enabled ? "ON" : "OFF"}.</b> <span className="mu">{d.enabled ? "Emails use the active version of each template." : "Every email uses the built-in wording."}</span></span>
        <button className="b g sm" onClick={flip} data-testid="etpl-switch-btn">{d.enabled ? "Turn off (use built-in wording)" : "Turn back on"}</button>
      </div>
    </section>);
}

export default function EmailTemplates() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [lang, setLang] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/email-templates").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => (d?.templates || []).filter((t) => {
    const s = `${t.name} ${t.key} ${t.langs.en.subject} ${t.langs.sw.subject}`.toLowerCase();
    const ls = lang ? [t.langs[lang]] : [t.langs.en, t.langs.sw];
    const st = (l) => (l.status !== "active" ? "archived" : l.drafts ? "draft" : l.source === "builtin" ? "builtin" : "edited");
    return (!q || s.includes(q.toLowerCase())) && (!cat || t.category === cat) && (!status || ls.some((l) => st(l) === status || (status === "draft" && l.drafts)));
  }), [d, q, cat, lang, status]);
  if (!d) return <p className="mu">Loading…</p>;
  return (
    <div className="ch chs phd" data-testid="email-templates">
      <h2 style={{ margin: 0 }}>Email templates</h2>
      <p className="mu">Wording of every email the system sends. Triggers, recipients, links and amounts are set by the system and can't be changed here. Nothing is sent from this page.</p>
      <Switch d={d} reload={load} />
      <div className="r" style={{ gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <input className="sel" style={{ flex: "1 1 220px" }} placeholder="Search name, key or subject" value={q} onChange={(e) => setQ(e.target.value)} data-testid="etpl-search" />
        <select className="sel" style={{ width: "auto" }} value={cat} onChange={(e) => setCat(e.target.value)} data-testid="etpl-filter-category"><option value="">All categories</option>{d.categories.map((c) => <option key={c}>{c}</option>)}</select>
        <select className="sel" style={{ width: "auto" }} value={lang} onChange={(e) => setLang(e.target.value)} data-testid="etpl-filter-lang"><option value="">Both languages</option><option value="en">English</option><option value="sw">Kiswahili</option></select>
        <select className="sel" style={{ width: "auto" }} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="etpl-filter-status"><option value="">Any status</option><option value="builtin">Active · built-in</option><option value="edited">Active · edited</option><option value="draft">Has a draft</option><option value="archived">Archived</option></select>
      </div>
      <p className="mu" style={{ fontSize: 12 }} data-testid="etpl-count">{rows.length} of {d.templates.length} templates</p>
      {rows.map((t) => (
        <section key={t.key} className="box" style={{ marginBottom: 8, padding: "12px 14px" }} data-testid={`etpl-row-${t.key}`}>
          <div className="r sp" style={{ gap: 8, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}><b>{t.name}</b> <span className="chip" style={{ "--c": "var(--mu)" }}>{t.category}</span>{t.risk && <> <span className="chip" style={{ "--c": "var(--wa, #d97706)" }}>{t.risk}</span></>}
              <small className="mu" style={{ display: "block" }}>#{t.flow} · <code>{t.key}</code></small></div>
            <button className="b g sm" onClick={() => setOpen(t.key)} data-testid={`etpl-open-${t.key}`}>Open</button>
          </div>
          {Object.entries(LANGS).filter(([k]) => !lang || k === lang).map(([k, l]) => (
            <div key={k} className="r" style={{ gap: 8, marginTop: 6, flexWrap: "wrap", fontSize: 13 }}>
              <span className="chip" style={{ "--c": "var(--ac)" }}>{l}</span><StatusChip l={t.langs[k]} id={`etpl-status-${t.key}-${k}`} />
              <span style={{ flex: "1 1 200px", minWidth: 0, overflowWrap: "anywhere" }}>{t.langs[k].subject}</span>
              <small className="mu">{fmt(t.langs[k].updated_at)} · {t.langs[k].updated_by || "—"}</small>
            </div>))}
        </section>))}
      {open && <EmailTemplateEditor tkey={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>);
}
